import { TARGET_SAMPLE_RATE, type TextSegment } from '@renderer/services/audio-transcription/types'
import {
  classifyMeeting,
  decideUserVoice,
  MAX_EMBEDDINGS_PER_MEETING,
  MAX_SAMPLE_ECHO_SHARE,
  isSessionSettled,
  needsMoreSamples,
  type MicEpochVerdict,
  type MicVoiceEvidence,
  type SelfVoiceFirstMatch,
  type SelfVoiceSessionSummary,
} from '@shared/self-voice'
import { MicAudioHistory } from './mic-audio-history'
import type { VoiceEmbedder } from './voice-embedder'

/** Finals arrive a few seconds after their speech, so this much mic audio is kept to match them. */
const HISTORY_SECONDS = 30
/** Trimmed off each end of an utterance, since its times are only approximately aligned to the audio. */
const EDGE_TRIM_MS = 300
/** At most this much of an utterance is embedded, from its end, since a long turn's start may have left the history. */
export const MAX_EMBED_MS = 6_000
/** Less voiced audio than this gives an unreliable embedding. */
export const MIN_VOICED_MS = 1_500
/** Embeddings start at least this far apart, which holds the model to roughly a fifth of one core. */
export const MIN_EMBED_GAP_MS = 4_000

const FRAME = TARGET_SAMPLE_RATE / 50
/** Frames quieter than this, or than a tenth of the clip's loudest frame, are pauses. */
const MIN_VOICED_RMS = 0.005

/** The clip's 20 ms frames that carry speech, joined. */
export function keepVoicedFrames(samples: Float32Array): Float32Array {
  const levels: Array<number> = []
  for (let start = 0; start + FRAME <= samples.length; start += FRAME) {
    let sum = 0
    for (let i = start; i < start + FRAME; i++) sum += samples[i] * samples[i]
    levels.push(Math.sqrt(sum / FRAME))
  }
  const floor = Math.max(MIN_VOICED_RMS, Math.max(0, ...levels) / 10)
  const voiced = levels.flatMap((level, i) => (level >= floor ? [i] : []))
  const out = new Float32Array(voiced.length * FRAME)
  voiced.forEach((frame, i) => out.set(samples.subarray(frame * FRAME, (frame + 1) * FRAME), i * FRAME))
  return out
}

function overlapMs(a: [number, number], b: [number, number]): number {
  return Math.max(0, Math.min(a[1], b[1]) - Math.max(a[0], b[0]))
}

/** Adds a span to sorted, disjoint spans, merging any it touches, so two far-end voices talking at once count once. */
export function addMergedSpan(spans: Array<[number, number]>, span: [number, number]): void {
  const merged: [number, number] = [span[0], span[1]]
  const kept = spans.filter((s) => {
    if (s[1] < merged[0] || s[0] > merged[1]) return true
    merged[0] = Math.min(merged[0], s[0])
    merged[1] = Math.max(merged[1], s[1])
    return false
  })
  kept.push(merged)
  kept.sort((a, b) => a[0] - b[0])
  spans.splice(0, spans.length, ...kept)
}

/** One mic epoch's voices. `mic:1` in one epoch can be someone else in the next. */
interface MicEpoch {
  voices: Record<string, MicVoiceEvidence>
  utterances: Record<string, Array<[number, number]>>
  /** The stretch of audio each embedding came from, in the order of the voice's embeddings. */
  sampledClips: Record<string, Array<[number, number]>>
  sentSpeakerKey: string | null
  /** From the epoch's first mic utterance to its last. */
  span: [number, number] | null
}

function micSpeechMs(epoch: MicEpoch): number {
  return Object.values(epoch.voices).reduce((acc, v) => acc + v.speechMs, 0)
}

export interface SelfVoiceSessionOptions {
  /** Whether to match voices against the fingerprint, which main holds and scores against. */
  matching: boolean
  /** The embedding's similarity to the fingerprint, from main, or null when main won't score it. */
  scoreEmbedding: (embedding: Array<number>) => Promise<number | null>
  loadEmbedder: () => Promise<VoiceEmbedder>
  /**
   * Tells the audio session which mic voice is the user in one mic epoch. Returns false when it couldn't be sent.
   * Without it, matching only reports the first match.
   */
  sendVerdict?: (verdict: MicEpochVerdict) => boolean
  onFirstMatch: (match: SelfVoiceFirstMatch) => void
  isMergedAudio: () => boolean
}

/**
 * One meeting's self-voice matching. Embeds a sample of each mic voice's
 * utterances, scores them against the fingerprint, and unloads the model once
 * the meeting is settled; a new mic voice loads it again. Each mic epoch is
 * judged on its own, since a fresh Deepgram stream numbers voices from 0.
 */
export class SelfVoiceSession {
  private readonly history = new MicAudioHistory(HISTORY_SECONDS)
  private readonly epochs = new Map<number, MicEpoch>()
  private latestEpoch = 0
  /** Far-end speech, merged into disjoint spans. */
  private readonly systemSpeech: Array<[number, number]> = []
  private embedder: Promise<VoiceEmbedder> | null = null
  private embedding = false
  private lastEmbedAtMs = 0
  private embeddingCount = 0
  private modelLoadMs: number | null = null
  private mergedAudio = false
  private finished = false
  private matched = false
  private readonly startedAtMs = Date.now()

  constructor(private readonly options: SelfVoiceSessionOptions) {}

  private get hasFingerprint(): boolean {
    return this.options.matching
  }

  addMicAudio(pcm16: ArrayBuffer, atMs: number): void {
    this.history.append(pcm16, atMs)
  }

  addTranscript(segment: TextSegment): void {
    if (this.finished) return
    const span: [number, number] = [segment.startTime, segment.endTime]
    if (!segment.isHost) {
      addMergedSpan(this.systemSpeech, span)
      return
    }
    if (segment.speaker === undefined) return
    this.mergedAudio ||= this.options.isMergedAudio()
    const micEpoch = segment.micEpoch ?? 0
    this.latestEpoch = Math.max(this.latestEpoch, micEpoch)
    const epoch = this.epochAt(micEpoch)
    const key = `mic:${segment.speaker}`
    const voice = (epoch.voices[key] ??= { speechMs: 0, echoMs: 0, embeddings: [], echoShares: [], scores: [] })
    voice.speechMs += span[1] - span[0]
    ;(epoch.utterances[key] ??= []).push(span)
    epoch.span = epoch.span ? [Math.min(epoch.span[0], span[0]), Math.max(epoch.span[1], span[1])] : span
    void this.sampleUtterance(micEpoch, key, span)
  }

  private epochAt(micEpoch: number): MicEpoch {
    let epoch = this.epochs.get(micEpoch)
    if (!epoch) {
      epoch = { voices: {}, utterances: {}, sampledClips: {}, sentSpeakerKey: null, span: null }
      this.epochs.set(micEpoch, epoch)
    }
    return epoch
  }

  /** The stretch of an utterance that gets embedded. */
  private clipWindow(span: [number, number]): [number, number] {
    const end = span[1] - EDGE_TRIM_MS
    return [Math.max(span[0] + EDGE_TRIM_MS, end - MAX_EMBED_MS), end]
  }

  private readClip(window: [number, number]): Float32Array | null {
    const clip = this.history.slice(window[0], window[1])
    if (!clip) return null
    const voiced = keepVoicedFrames(clip)
    return voiced.length >= (MIN_VOICED_MS / 1000) * TARGET_SAMPLE_RATE ? voiced : null
  }

  private async sampleUtterance(micEpoch: number, key: string, span: [number, number]): Promise<void> {
    const now = Date.now()
    if (
      this.embedding ||
      now - this.lastEmbedAtMs < MIN_EMBED_GAP_MS ||
      this.embeddingCount >= MAX_EMBEDDINGS_PER_MEETING
    )
      return
    const epoch = this.epochAt(micEpoch)
    if (!needsMoreSamples(epoch.voices[key], this.hasFingerprint)) return
    const window = this.clipWindow(span)
    // The far end heard so far; its later finals are checked again when the meeting ends.
    if (this.echoShare(window) > MAX_SAMPLE_ECHO_SHARE) return
    const clip = this.readClip(window)
    if (!clip) return
    this.embedding = true
    this.lastEmbedAtMs = now
    try {
      const embedder = await this.loadEmbedder()
      const embedding = await embedder.embed(clip)
      const score = this.hasFingerprint ? await this.options.scoreEmbedding(embedding) : null
      if (this.finished) return
      this.embeddingCount += 1
      const voice = epoch.voices[key]
      voice.embeddings.push(embedding)
      ;(epoch.sampledClips[key] ??= []).push(window)
      if (score !== null) voice.scores.push(score)
      this.sendVerdictIfChanged(micEpoch, epoch)
      // Older epochs are over, so only the latest one can still need the model.
      const latest = this.epochAt(this.latestEpoch)
      if (isSessionSettled(latest.voices, this.hasFingerprint) || this.embeddingCount >= MAX_EMBEDDINGS_PER_MEETING) {
        this.unloadEmbedder()
      }
    } catch (error) {
      // Finishing the meeting disposes the model under an embedding in flight.
      if (this.finished) return
      console.error('[SelfVoice] Embedding failed; matching is off for this meeting', error)
      this.embeddingCount = MAX_EMBEDDINGS_PER_MEETING
      this.unloadEmbedder()
    } finally {
      this.embedding = false
    }
  }

  private loadEmbedder(): Promise<VoiceEmbedder> {
    if (!this.embedder) {
      const startedAt = performance.now()
      this.embedder = this.options.loadEmbedder().then((embedder) => {
        this.modelLoadMs ??= Math.round(performance.now() - startedAt)
        console.log(`[SelfVoice] Voice model loaded in ${Math.round(performance.now() - startedAt)} ms`)
        return embedder
      })
      this.embedder.catch(() => (this.embedder = null))
    }
    return this.embedder
  }

  private unloadEmbedder(): void {
    const embedder = this.embedder
    if (!embedder) return
    this.embedder = null
    void embedder.then((e) => e.dispose()).catch(() => {})
    console.log(`[SelfVoice] Voice model unloaded after ${this.embeddingCount} embeddings`)
  }

  private sendVerdictIfChanged(micEpoch: number, epoch: MicEpoch): void {
    if (!this.hasFingerprint) return
    const verdict = decideUserVoice(epoch.voices)
    if (verdict.speakerKey && !this.matched) {
      this.matched = true
      this.options.onFirstMatch({
        ...verdict,
        micEpoch,
        elapsedMs: Date.now() - this.startedAtMs,
        micVoices: Object.keys(epoch.voices).length,
        meetingType: classifyMeeting({
          voices: epoch.voices,
          systemSpeechMs: this.systemSpeechMs(),
          mergedAudio: this.mergedAudio,
        }),
      })
    }
    if (verdict.speakerKey === epoch.sentSpeakerKey || !this.options.sendVerdict) return
    if (this.options.sendVerdict({ ...verdict, micEpoch })) epoch.sentSpeakerKey = verdict.speakerKey
  }

  /** System-audio speech in the meeting, or only within `span` when given. */
  private systemSpeechMs(span?: [number, number]): number {
    return this.systemSpeech.reduce((acc, sys) => acc + (span ? overlapMs(sys, span) : sys[1] - sys[0]), 0)
  }

  /** The share of a stretch of mic audio that overlapped system-audio speech. */
  private echoShare(span: [number, number]): number {
    const length = span[1] - span[0]
    return length > 0 ? Math.min(1, this.systemSpeechMs(span) / length) : 0
  }

  /** Reports the epoch with the most mic speech, which teaches or re-checks the fingerprint. */
  finish(): SelfVoiceSessionSummary {
    this.finished = true
    this.unloadEmbedder()
    const main = [...this.epochs.values()].reduce<MicEpoch | null>(
      (best, epoch) => (!best || micSpeechMs(epoch) > micSpeechMs(best) ? epoch : best),
      null,
    ) ?? { voices: {}, utterances: {}, sampledClips: {}, sentSpeakerKey: null, span: null }
    for (const [key, utterances] of Object.entries(main.utterances)) {
      main.voices[key].echoMs = utterances.reduce(
        (acc, mic) => acc + this.systemSpeech.reduce((sum, sys) => sum + overlapMs(mic, sys), 0),
        0,
      )
    }
    for (const [key, clips] of Object.entries(main.sampledClips)) {
      main.voices[key].echoShares = clips.map((clip) => this.echoShare(clip))
    }
    // With several epochs, only the far end heard alongside this one says whether it was a remote call.
    const epochSpan = this.epochs.size > 1 && main.span ? main.span : undefined
    return {
      voices: main.voices,
      systemSpeechMs: this.systemSpeechMs(epochSpan),
      mergedAudio: this.mergedAudio,
      verdict: decideUserVoice(main.voices),
      embeddingCount: this.embeddingCount,
      modelLoadMs: this.modelLoadMs,
      micEpochs: this.epochs.size,
    }
  }
}
